import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import UniverseCard from "@/components/UniverseCard";
import ThisDayInLore from "@/components/ThisDayInLore";
import TrendingSection from "@/components/TrendingSection";
import SEOHead from "@/components/SEOHead";
import { Search, Sparkles, Globe, Users, Map } from "lucide-react";
import { Input } from "@/components/ui/input";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/f60715c2-3b6b-4c99-a6c8-5c96b98e6cf9/images/53108f7d8ec9c28606e03e3c421e51fe4b80f13705cd9eeafc38c88182bd88ad.png";

const HomePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [universes, setUniverses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const category = searchParams.get("category");
  const universesRef = useRef(null);

  useEffect(() => {
    const fetchUniverses = async () => {
      try {
        const url = category ? `${API}/universes?category=${category}` : `${API}/universes`;
        const response = await axios.get(url);
        setUniverses(response.data);
      } catch (error) {
        console.error("Failed to fetch universes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUniverses();
  }, [category]);

  // Scroll to universe grid when category changes via navbar
  useEffect(() => {
    if (category && universesRef.current) {
      setTimeout(() => {
        universesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [category]);

  const filteredUniverses = universes.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [
    { id: "sci-fi", name: "Sci-Fi", icon: Sparkles, count: universes.filter(u => u.category === "sci-fi").length },
    { id: "fantasy", name: "Fantasy", icon: Globe, count: universes.filter(u => u.category === "fantasy").length },
    { id: "superhero", name: "Superhero", icon: Users, count: universes.filter(u => u.category === "superhero").length },
    { id: "horror", name: "Horror", icon: Map, count: universes.filter(u => u.category === "horror").length },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <SEOHead />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center pt-16" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Fan World" 
            className="w-full h-full object-cover"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <span className="inline-block text-xs font-mono tracking-[0.2em] uppercase text-amber-500 mb-6 animate-fade-in">
            {t("home.tagline")}
          </span>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white mb-6 font-['Outfit'] animate-fade-in stagger-1 text-glow hero-title">
            {t("home.heroTitle1")}<br />
            <span className="text-yellow-500">{t("home.heroTitle2")}</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-8 animate-fade-in stagger-2">
            {t("home.heroDesc")}
          </p>
          
          {/* Search Bar */}
          <form onSubmit={(e) => { e.preventDefault(); if (searchTerm.trim()) navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`); }} className="relative max-w-xl mx-auto animate-fade-in stagger-3">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder={t("home.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 input-dark text-lg"
              data-testid="search-input"
            />
          </form>
        </div>
      </section>

      {/* This Day in Lore */}
      <section className="pt-12 pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ThisDayInLore />
        </div>
      </section>

      {/* Trending */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TrendingSection />
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/"
              className={`flex items-center gap-2 px-6 py-3 transition-all ${
                !category ? "btn-primary" : "btn-secondary"
              }`}
              data-testid="category-all"
            >
              {t("home.allUniverses")}
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/?category=${cat.id}`}
                className={`flex items-center gap-2 px-6 py-3 transition-all ${
                  category === cat.id ? "btn-primary" : "btn-secondary"
                }`}
                data-testid={`category-${cat.id}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.name}
                <span className="text-xs opacity-60">({cat.count})</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Universe Grid */}
      <section className="py-16 flex-1" id="universes" ref={universesRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit']">
              {category ? `${category.charAt(0).toUpperCase() + category.slice(1)} Universes` : "All Universes"}
            </h2>
            <span className="text-slate-400 text-sm">{filteredUniverses.length} universes</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : filteredUniverses.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400 text-lg">No universes found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="universe-grid">
              {filteredUniverses.map((universe, index) => (
                <div key={universe.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
                  <UniverseCard universe={universe} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0B0F19] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-black text-yellow-500 font-['Outfit']">{universes.length}</div>
              <div className="text-sm text-slate-400 mt-2">{t("home.universes")}</div>
            </div>
            <div>
              <div className="text-4xl font-black text-yellow-500 font-['Outfit']">100+</div>
              <div className="text-sm text-slate-400 mt-2">{t("home.characters")}</div>
            </div>
            <div>
              <div className="text-4xl font-black text-yellow-500 font-['Outfit']">50+</div>
              <div className="text-sm text-slate-400 mt-2">{t("home.maps")}</div>
            </div>
            <div>
              <div className="text-4xl font-black text-yellow-500 font-['Outfit']">{t("home.weekly")}</div>
              <div className="text-sm text-slate-400 mt-2">{t("home.contentUpdates")}</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
